// @flow

import React from 'react';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';
import type { Theme } from '../../../types';
import { media } from '../../../helpers';
import {
  toggleActivePdbId,
  isPdbIdInComparison,
  isPdbComparisonFull,
  getComparisonText,
} from '../../../store/pdbComparison';
import { compareEnabled } from '../../../store/compareSettings';
import { isAvailable as isAvailableSelector, hasVbmr } from '../../../store/pdb/selectors';
import { getVbmrAvailability, getLabel } from '../../../store/vbmr';
import { getSelectedFilters } from '../../../store/assortment';
import { FILTER_DELIVERY_DATE, FILTER_DELIVERY_VBMR } from '../../../store/assortment/helpers/applyCustomFilter';
import { DefaultStyledContainer as CarblockContainer } from '../../carBlock/containers';
import { Promotion } from '../../carBlock/components';
import Carblock from '../../carBlock/carBlock.container';
import CardComponent from '../../card';
import { Checkbox as CheckboxComponent } from '../../form';

const Card = styled<typeof CardComponent, Theme, _>(CardComponent)`
  justify-content: space-between;
  padding: 0;
  width: 100%;

  & > div {
    margin-bottom: 0;
  }

  ${/* sc-sel */CarblockContainer} {
    box-sizing: border-box;
    padding: calc(${props => props.theme.card.padding} / 2) ${props => props.theme.card.padding} ${props => props.theme.card.padding};
    ${media.lg(css`
      padding: calc(${props => props.theme.card.padding} / 2) ${props => props.theme.card.padding};
    `)}

    ${/* sc-sel */Promotion} {
      left: 0;
      top: 1rem;
    }
  }
`;

const Label = styled<any, Theme>('label')`
  align-items: center;
  background-color: ${props => props.theme.assortment.carBlock.comparisonLabelBackground};
  border-radius: 0 0 ${props => props.theme.card.borderRadius} ${props => props.theme.card.borderRadius};
  box-sizing: border-box;
  display: none;
  font-size: 0.75rem;
  padding: 1rem ${props => props.theme.card.padding};
  ${media.lg(css`
    display: flex;
  `)}
`;

const Checkbox = styled<typeof CheckboxComponent, Theme, _>(CheckboxComponent)`
  margin-right: 0.5rem;
`;

const createTags = (
  vbmrAvailable: boolean,
  filters: ?Array<Object> = [],
  label: string,
): ?Array<string> => {
  const quickDelivery: boolean = filters ? filters.filter(
    filter => filter.id === FILTER_DELIVERY_DATE,
  ).length > 0 : false;
  if (vbmrAvailable && quickDelivery) {
    return [label];
  }
  return null;
};

const buildVbmrQueryData = (
  vbmrAvailable: boolean,
  filters: Array<Object> = [],
): Object => {
  const customFilterKeys: Array<string> = [FILTER_DELIVERY_DATE, FILTER_DELIVERY_VBMR];
  if (vbmrAvailable && filters.some(filter => customFilterKeys.includes(filter.id))) {
    return { vbmr: 1 };
  }
  return {};
};

type OwnProps = {|
  pdbId: string,
|};

type StateProps = {|
  isInComparison: boolean,
  isComparisonFull: boolean,
  isAvailable: boolean,
  isCompareEnabled: boolean,
  comparisonText: string,
  isVbmr: boolean,
  selectedFilters: any,
  labelVbmr: string,
  vbmrAvailable: boolean,
|};

type DispatchProps = {|
  togglePdbIdCompare: Function,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  ...DispatchProps,
|};

const AssortmentCarblock = ({
  pdbId,
  togglePdbIdCompare,
  isInComparison,
  isComparisonFull,
  isAvailable,
  isCompareEnabled,
  comparisonText,
  isVbmr,
  selectedFilters,
  labelVbmr,
  vbmrAvailable,
}: Props) => (
  <Card backgroundColor="default">
    <Carblock
      tags={createTags(isVbmr && vbmrAvailable, selectedFilters, labelVbmr)}
      pdbId={pdbId}
      queryData={buildVbmrQueryData(isVbmr && vbmrAvailable, selectedFilters)}
    />
    {isAvailable && isCompareEnabled && (
      <Label>
        <Checkbox
          onChange={() => togglePdbIdCompare(pdbId)}
          checked={isInComparison}
          disabled={isComparisonFull && !isInComparison}
          id={`assortment-${pdbId}`}
          name={`assortment-${pdbId}`}
        />
        {' '}
        {comparisonText}
      </Label>
    )}
  </Card>
);

const mapStateToProps = (state: any, { pdbId }: OwnProps): StateProps => ({
  isInComparison: isPdbIdInComparison(state, pdbId),
  isComparisonFull: isPdbComparisonFull(state),
  isAvailable: isAvailableSelector(state, pdbId),
  isCompareEnabled: compareEnabled(state),
  comparisonText: getComparisonText(state, pdbId),
  isVbmr: hasVbmr(state, pdbId),
  selectedFilters: getSelectedFilters(state),
  labelVbmr: getLabel(state),
  vbmrAvailable: getVbmrAvailability(state),
});

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  togglePdbIdCompare: (pdbId: string) => dispatch(toggleActivePdbId(pdbId)),
});

export default connect<Props, OwnProps, StateProps, DispatchProps, any, any>(
  mapStateToProps,
  mapDispatchToProps,
)(AssortmentCarblock);
